import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { AxiosError, AxiosResponse } from "axios"
import { useInfiniteQuery, useQuery } from "react-query"
import { IGroup } from "ts/interfaces/Group"

import { IPaginatedGroupMembers } from "./groups.types"
import {
  IInfiniteQuerySchoolGroupCandidatesArgs,
  IInfiniteQuerySchoolGroupMembersArgs,
  IInfiniteQuerySchoolGroupNewCandidatesArgs,
  IQuerySchoolGroupImportTemplateArgs,
  IQuerySchoolGroupMembersArgs,
  IQuerySchoolGroupsArgs,
} from "./schoolGroups.types"

export const schoolGroupsQueryKey = () => ["schoolGroups"]
export const schoolGroupMembersQueryKey = (
  groupId: number,
  skip?: number,
  limit?: number,
) => ["schoolGroupMembers", groupId, skip, limit]
export const infiniteSchoolGroupMembersQueryKey = (
  groupId: number,
  limit?: number,
) => ["schoolGroupMembersInfinite", groupId, limit]
export const schoolGroupCandidatesQueryKey = (
  groupId: number,
  filter?: string,
  limit?: number,
) => ["schoolGroupCandidates", groupId, filter, limit]
export const schoolGroupNewCandidatesQueryKey = (
  filter?: string,
  limit?: number,
) => ["schoolGroupNewCandidates", filter, limit]

const getGroupMembersNextPageParam = (
  lastPage: AxiosResponse<IPaginatedGroupMembers>,
  allPages: AxiosResponse<IPaginatedGroupMembers>[],
  limit: number,
) => {
  const loadedItemCount = allPages.reduce(
    (acc, page) => acc + page.data.objects.length,
    0,
  )
  const totalItems = lastPage.data.all_objects_count
  if (typeof totalItems === "number") {
    return loadedItemCount < totalItems ? loadedItemCount : undefined
  }
  return lastPage.data.objects.length === limit ? loadedItemCount : undefined
}

export const useQuerySchoolGroups = (args?: IQuerySchoolGroupsArgs) =>
  useQuery<AxiosResponse<IGroup[]>, AxiosError>(
    schoolGroupsQueryKey(),
    async () => axiosInstance(Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS),
    { ...args?.options },
  )

const schoolGroupMembersUrl = (groupId: number) =>
  Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_GROUPID_MEMBERS.replace(
    "GROUPID",
    `${groupId}`,
  )

const schoolGroupCandidatesUrl = (groupId: number) =>
  Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_GROUPID_CANDIDATES.replace(
    "GROUPID",
    `${groupId}`,
  )

export const useQuerySchoolGroupMembers = ({
  groupId,
  skip = 0,
  limit = 100,
  options,
}: IQuerySchoolGroupMembersArgs) =>
  useQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    schoolGroupMembersQueryKey(groupId, skip, limit),
    async () =>
      axiosInstance(schoolGroupMembersUrl(groupId), {
        params: { skip, limit },
      }),
    { ...options },
  )

export const useInfiniteQuerySchoolGroupMembers = ({
  groupId,
  limit = 100,
  options,
}: IInfiniteQuerySchoolGroupMembersArgs) =>
  useInfiniteQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    infiniteSchoolGroupMembersQueryKey(groupId, limit),
    async ({ pageParam = 0 }) =>
      axiosInstance(schoolGroupMembersUrl(groupId), {
        params: { skip: pageParam, limit },
      }),
    {
      ...options,
      getNextPageParam: (lastPage, allPages) =>
        getGroupMembersNextPageParam(lastPage, allPages, limit),
    },
  )

export const useInfiniteQuerySchoolGroupCandidates = ({
  groupId,
  filter,
  limit = 30,
  options,
}: IInfiniteQuerySchoolGroupCandidatesArgs) =>
  useInfiniteQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    schoolGroupCandidatesQueryKey(groupId, filter, limit),
    async ({ pageParam = 0 }) =>
      axiosInstance(schoolGroupCandidatesUrl(groupId), {
        params: {
          skip: pageParam,
          limit,
          filter: filter?.length ? filter : null,
        },
      }),
    {
      ...options,
      getNextPageParam: (lastPage, allPages) =>
        getGroupMembersNextPageParam(lastPage, allPages, limit),
    },
  )

// Purpose-built for the Create flow — candidates for a group that doesn't exist yet,
// so unlike useInfiniteQuerySchoolGroupCandidates there's no group_id to scope by.
export const useInfiniteQuerySchoolGroupNewCandidates = ({
  filter,
  limit = 30,
  options,
}: IInfiniteQuerySchoolGroupNewCandidatesArgs) =>
  useInfiniteQuery<AxiosResponse<IPaginatedGroupMembers>, AxiosError>(
    schoolGroupNewCandidatesQueryKey(filter, limit),
    async ({ pageParam = 0 }) =>
      axiosInstance(Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_CANDIDATES, {
        params: {
          skip: pageParam,
          limit,
          filter: filter?.length ? filter : null,
        },
      }),
    {
      ...options,
      getNextPageParam: (lastPage, allPages) =>
        getGroupMembersNextPageParam(lastPage, allPages, limit),
    },
  )

export const useQuerySchoolGroupImportTemplate = ({
  options,
}: IQuerySchoolGroupImportTemplateArgs = {}) =>
  useQuery<AxiosResponse<Blob>, AxiosError>(
    ["schoolGroupImportTemplate"],
    async () =>
      axiosInstance(Endpoint.TEACHER_ADMIN_SCHOOL_GROUPS_IMPORT_TEMPLATE, {
        responseType: "blob",
      }),
    { enabled: false, ...options },
  )
